import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import { getExpectationsSurvey } from "../services/surveyService";

// Surveys (founder go 2026-10-05: "build it with names"). The expectations
// survey, "What did you expect from Curiouzz?", emailed once to members who
// have been shown at least one introduction. Answers land here as they come
// in: the page reloads itself every minute while it is open.
//
// Read only. Each one-tap answer is split by gender and by whether the member
// has had a reveal, because "Not what I expected" came mostly from women who
// had revealed. Test accounts are hidden unless asked for.
const REFRESH_MS = 60 * 1000;

const etTime = (d) =>
  d
    ? new Date(d).toLocaleString("en-CA", {
        timeZone: "America/Toronto",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }) + " ET"
    : "None yet";

const genderLabel = (g) =>
  g === "woman" ? "Woman" : g === "man" ? "Man" : g === "beyond_binary" ? "Beyond binary" : "Gender not given";

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const SurveyResultsLayer = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [includeTest, setIncludeTest] = useState(false);
  const [onlyWords, setOnlyWords] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async (withTest, quiet) => {
    if (!quiet) setLoading(true);
    setErr("");
    try {
      const res = await getExpectationsSurvey({ includeTest: withTest ? 1 : 0 });
      // The server answers { status, data: {...} } and axios wraps that in
      // its own .data, so the survey itself is one level deeper.
      setData(res?.data?.data || null);
    } catch (e) {
      setErr("Could not load the survey answers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(includeTest, false);
    clearInterval(timer.current);
    timer.current = setInterval(() => load(includeTest, true), REFRESH_MS);
    return () => clearInterval(timer.current);
  }, [includeTest, load]);

  const answered = data?.answered || 0;
  const emailed = data?.emailed || 0;
  const rate = emailed ? Math.round((answered / emailed) * 100) : null;
  const people = (data?.people || []).filter((p) => !onlyWords || p.ownWords);

  const stat = (label, value, icon, sub) => (
    <div className="col-sm-3 col-6">
      <div className="p-16 radius-8 bg-neutral-50 h-100">
        <div className="d-flex align-items-center gap-2 mb-1">
          <Icon icon={icon} className="text-secondary-light" />
          <span className="text-sm text-secondary-light">{label}</span>
        </div>
        <h5 className="mb-0 fw-semibold">{value}</h5>
        {sub ? <div className="text-xs text-secondary-light mt-1">{sub}</div> : null}
      </div>
    </div>
  );

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 cz-toolbar">
        <div className="cz-topline">
          <div>
            <h6 className="mb-1 fw-semibold">What did you expect from Curiouzz?</h6>
            <p className="text-sm text-secondary-light mb-0" style={{ maxWidth: 680 }}>
              Emailed once to members who have been shown at least one introduction.
              Answers appear here as they come in, and the page refreshes itself every minute.
            </p>
          </div>
          <div className="cz-actions">
            <label className="d-flex align-items-center gap-2 text-sm mb-0">
              <input
                type="checkbox"
                className="form-check-input mt-0"
                checked={includeTest}
                onChange={(e) => setIncludeTest(e.target.checked)}
              />
              Show test accounts
            </label>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
              onClick={() => load(includeTest, false)}
              disabled={loading}
            >
              <Icon icon="mdi:refresh" /> Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="card-body p-24">
        {err ? <div className="alert alert-danger py-2">{err}</div> : null}

        <div className="row g-3 mb-24">
          {stat("Emailed", loading && !data ? "…" : emailed, "mdi:email-outline", emailed ? null : "Not sent yet")}
          {stat(
            "Answered",
            loading && !data ? "…" : answered,
            "mdi:clipboard-check-outline",
            rate !== null ? `${rate}% of those emailed` : null
          )}
          {stat(
            "Women and men",
            loading && !data ? "…" : `${data?.answeredWomen || 0} / ${data?.answeredMen || 0}`,
            "mdi:account-group-outline",
            "Answered, women / men"
          )}
          {stat("Last answer", loading && !data ? "…" : etTime(data?.lastAnsweredAt), "mdi:clock-outline")}
        </div>

        <div className="row g-3 mb-24">
          {(data?.questions || []).map((q, qi) => {
            const max = Math.max(1, ...q.options.map((o) => o.total));
            return (
              <div className="col-lg-6" key={q.key}>
                <div className="p-16 radius-8 border h-100">
                  <div className="text-xs fw-semibold text-secondary-light mb-1">QUESTION {qi + 1}</div>
                  <h6 className="text-md fw-semibold mb-12">{q.text}</h6>
                  <div className="d-flex flex-column gap-2">
                    {q.options.map((o) => (
                      <div key={o.code}>
                        <div className="d-flex justify-content-between align-items-baseline gap-2">
                          <span className="text-sm">{o.label}</span>
                          <span className="text-sm fw-semibold" style={{ fontVariantNumeric: "tabular-nums" }}>
                            {o.total}
                          </span>
                        </div>
                        <div className="radius-4 bg-neutral-100" style={{ height: 8, overflow: "hidden" }}>
                          <div
                            style={{
                              width: `${(o.total / max) * 100}%`,
                              height: "100%",
                              background: "#7C4DFF",
                              borderRadius: 4,
                            }}
                          />
                        </div>
                        {o.total ? (
                          <div className="text-xs text-secondary-light mt-1">
                            Women {o.women}, men {o.men}
                            {o.other ? `, other ${o.other}` : ""}; {o.revealed} had a reveal
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-12">
          <h6 className="text-md fw-semibold mb-0">Every answer, newest first</h6>
          <label className="d-flex align-items-center gap-2 text-sm mb-0">
            <input
              type="checkbox"
              className="form-check-input mt-0"
              checked={onlyWords}
              onChange={(e) => setOnlyWords(e.target.checked)}
            />
            Only people who wrote something
          </label>
        </div>

        {loading && !data ? (
          <div className="text-center text-secondary-light py-24">Loading…</div>
        ) : people.length === 0 ? (
          <div className="text-center text-secondary-light py-24">
            {answered ? "Nobody has written anything yet." : "No answers yet. They appear here the moment someone sends theirs."}
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {people.map((p) => (
              <div key={p.userId} className="p-16 radius-8 border">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-8">
                  <div>
                    <Link to={`/user/view-user/${p.userId}`} className="fw-semibold text-primary-600">
                      {p.name}
                    </Link>
                    <div className="text-xs text-secondary-light">
                      {genderLabel(p.gender)}; {plural(p.introductions, "introduction", "introductions")};{" "}
                      {plural(p.reveals, "reveal", "reveals")}
                      {p.test ? "; TEST ACCOUNT" : ""}
                      {p.deleted ? "; account since deleted" : ""}
                    </div>
                  </div>
                  <div className="text-xs text-secondary-light text-end">
                    {etTime(p.submittedAt)}
                    {p.timesSubmitted > 1 ? <div>Changed their answers ({p.timesSubmitted} sends)</div> : null}
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2 mb-8">
                  {p.answers.map((a, i) => (
                    <span
                      key={a.key}
                      className="text-xs radius-4 px-8 py-4 bg-neutral-50 border"
                      title={(data?.questions || [])[i]?.text}
                    >
                      <b>{i + 1}.</b> {a.label || "No answer"}
                    </span>
                  ))}
                </div>
                {p.ownWords ? (
                  <div>
                    <div className="text-xs text-secondary-light mb-1">5. {data?.ownWordsQuestion}</div>
                    <p className="mb-0 text-sm" style={{ whiteSpace: "pre-wrap" }}>
                      “{p.ownWords}”
                    </p>
                  </div>
                ) : (
                  <div className="text-xs text-secondary-light">5. Nothing written.</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveyResultsLayer;
